import { useSuspenseQuery } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';

import { StoreBadges } from '@/components/store-badges';
import { Wordmark } from '@/components/wordmark';
import { footerQuery, tenantQuery } from '@/queries';

/**
 * Footer. The store badges here are a contractual obligation, not decoration
 * (agreement §6 / AC-6.1), and they stay hidden behind the `apps_live` flag in
 * `site_config` until the apps ship.
 *
 * This was an async server component reading the data layer inline. That import
 * is what dragged the whole server half into the browser bundle, `node:async_hooks`
 * included,, so it reads through a query now like everything else.
 */
export function SiteFooter() {
  const { brands, config } = useSuspenseQuery(footerQuery).data;
  const tenant = useSuspenseQuery(tenantQuery).data;

  return (
    <footer className="mt-24 border-t border-line bg-panel">
      <div className="mx-auto grid max-w-[1180px] gap-10 px-[30px] py-14 sm:grid-cols-2 lg:grid-cols-4">
        <div className="lg:col-span-2">
          <Wordmark />
          <p className="mt-3 max-w-[38ch] text-[15px] leading-relaxed text-muted">
            Custom apparel and decoration. Stock, orders and production on one
            system.
          </p>
          {config.apps_live && (
            <div className="mt-6">
              <StoreBadges />
            </div>
          )}
        </div>

        <nav aria-labelledby="footer-brands">
          <h2 id="footer-brands" className="text-sm font-bold">
            Brands
          </h2>
          <ul className="mt-3 space-y-2.5 text-[15px] text-muted">
            {brands.map((b) => (
              <li key={b.id}>
                <Link to="/shop/$brand" params={{ brand: b.slug }} className="hover:text-brand-ink">
                  {b.name}
                </Link>
              </li>
            ))}
          </ul>
        </nav>

        <nav aria-labelledby="footer-help">
          <h2 id="footer-help" className="text-sm font-bold">
            Ordering
          </h2>
          <ul className="mt-3 space-y-2.5 text-[15px] text-muted">
            <li>
              <Link to="/custom" className="hover:text-brand-ink">
                Bulk and custom
              </Link>
            </li>
            <li>
              <Link to="/orders" className="hover:text-brand-ink">
                Track an order
              </Link>
            </li>
            <li>
              <Link to="/account" className="hover:text-brand-ink">
                Your account
              </Link>
            </li>
          </ul>
        </nav>
      </div>

      <div className="border-t border-line">
        <p className="mx-auto max-w-[1180px] px-[30px] py-6 text-[13px] text-muted">
          {/* The legal name, not the shop name: this line is who the money is
              paid to. They differ, and until R-140 both were hard-coded and
              one of them named the wrong company. */}
          Prices in {tenant.currency} and include GST. {tenant.legal_name}.
        </p>
      </div>
    </footer>
  );
}
